import React, { useState } from "react";
import {
    HiOutlineCalendar,
    HiOutlineUser,
    HiOutlineDotsVertical,
    HiOutlineEye,
    HiOutlinePencil,
    HiOutlineArchive,
    HiOutlineMail,
} from "react-icons/hi";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebase/config";
import { useSelector } from "react-redux";
import emailChanges from "../../../../data/emailChanges";
import "./TaskCard.scss";

const TaskCard = ({ task, employees, onStatusChange, onClick }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [updating, setUpdating] = useState(false);
    const currentUser = useSelector((state) => state.auth.user);
    const { isAdmin } = useSelector((state) => state.auth);

    // Helper functions
    const formatDate = (timestamp) => {
        if (!timestamp) return "No date set";

        let date;
        if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getDaysLeft = (dueDate) => {
        if (!dueDate) return { text: "No deadline", className: "" };

        let dueDateObj;
        if (dueDate.seconds) {
            dueDateObj = new Date(dueDate.seconds * 1000);
        } else {
            dueDateObj = new Date(dueDate);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const dueDay = new Date(dueDateObj);
        dueDay.setHours(0, 0, 0, 0);

        const diffTime = dueDay - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: "Overdue", className: "overdue" };
        } else if (diffDays === 0) {
            return { text: "Due today", className: "due-today" };
        } else if (diffDays <= 2) {
            return {
                text: `${diffDays} day${diffDays !== 1 ? "s" : ""} left`,
                className: "urgent",
            };
        } else {
            return { text: `${diffDays} days left`, className: "on-track" };
        }
    };

    const getAssigneeNames = () => {
        if (!task.assignees || task.assignees.length === 0) {
            return "Unassigned";
        }

        // If employees list is not available, just return the emails
        if (!employees || !Array.isArray(employees)) {
            return task.assignees.length === 1
                ? task.assignees[0]
                : `${task.assignees[0]} and ${
                      task.assignees.length - 1
                  } others`;
        }

        const assigneeList = task.assignees.map((email) => {
            const employee = employees.find((emp) => emp.email === email);
            return employee ? employee.name || email : email;
        });

        if (assigneeList.length === 1) {
            return assigneeList[0];
        } else if (assigneeList.length === 2) {
            return `${assigneeList[0]} and ${assigneeList[1]}`;
        } else {
            return `${assigneeList[0]} and ${assigneeList.length - 1} others`;
        }
    };

    const countComments = () => {
        return task.comments ? task.comments.length : 0;
    };

    const countAttachments = () => {
        return task.attachments ? task.attachments.length : 0;
    };

    const getStatusClass = () => {
        return task.statusNormalized || "new";
    };

    // Action handlers
    const handleMenuToggle = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleClickOutside = () => {
        if (showMenu) {
            setShowMenu(false);
        }
    };

    React.useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [showMenu]);

    const updateTaskStatus = async (e, newStatus) => {
        e.stopPropagation();
        setShowMenu(false);

        // Map normalized status back to the format expected by Firestore
        let firestoreStatus;
        if (newStatus === "new") firestoreStatus = "New task";
        else if (newStatus === "in-progress") firestoreStatus = "In progress";
        else firestoreStatus = "Completed";

        try {
            setUpdating(true);

            // Update task status in Firestore
            await updateDoc(doc(db, "assignedTasks", task.id), {
                status: firestoreStatus,
                lastUpdated: new Date(),
                lastUpdatedBy: currentUser.email || currentUser.uid,
                lastUpdatedIncident: `Status changed to ${firestoreStatus}`,
            });

            if (onStatusChange) {
                onStatusChange();
            }
        } catch (err) {
            console.error("Error updating task status:", err);
        } finally {
            setUpdating(false);
        }
    };

    const archiveTask = async (e) => {
        e.stopPropagation();
        setShowMenu(false);

        if (!window.confirm("Are you sure you want to archive this task?")) {
            return;
        }

        try {
            setUpdating(true);

            // Update task liveStatus to archived in Firestore
            await updateDoc(doc(db, "assignedTasks", task.id), {
                liveStatus: "archived",
                lastUpdated: new Date(),
                lastUpdatedBy: currentUser.email || currentUser.uid,
                lastUpdatedIncident: "Task archived",
            });

            if (onStatusChange) {
                onStatusChange();
            }
        } catch (err) {
            console.error("Error archiving task:", err);
        } finally {
            setUpdating(false);
        }
    };

    // New function to update assignee emails
    const updateAssigneeEmails = async (e) => {
        e.stopPropagation();
        setShowMenu(false);

        if (
            !window.confirm(
                "Are you sure you want to update assignee emails to the new domain?"
            )
        ) {
            return;
        }

        try {
            setUpdating(true);

            // Create a map of old to new emails for quick lookup
            const emailMap = {};
            emailChanges.forEach((change) => {
                emailMap[change.oldEmail] = change.newEmail;
            });

            let updated = false;
            let updatedAssignees = [];
            let updatedRoleBasedAssignees = [];

            // Update regular assignees if they exist
            if (task.assignees && task.assignees.length > 0) {
                updatedAssignees = task.assignees.map((email) => {
                    return emailMap[email] || email; // Use mapped email if exists, else keep original
                });

                // Check if anything changed
                if (
                    JSON.stringify(updatedAssignees) !==
                    JSON.stringify(task.assignees)
                ) {
                    updated = true;
                }
            }

            // Update role-based assignees if they exist
            if (
                task.roleBasedAssignment &&
                task.roleBasedAssignees &&
                task.roleBasedAssignees.length > 0
            ) {
                updatedRoleBasedAssignees = task.roleBasedAssignees.map(
                    (item) => {
                        if (emailMap[item.email]) {
                            return {
                                ...item,
                                email: emailMap[item.email],
                            };
                        }
                        return item;
                    }
                );

                // Check if anything changed
                if (
                    JSON.stringify(updatedRoleBasedAssignees) !==
                    JSON.stringify(task.roleBasedAssignees)
                ) {
                    updated = true;
                }
            }

            // Only update if there were changes
            if (updated) {
                const updateData = {
                    emailUpdated: true,
                    lastUpdated: new Date(),
                    lastUpdatedBy: currentUser.email || currentUser.uid,
                    lastUpdatedIncident: "Updated assignee emails",
                };

                // Add the updated assignees to update data
                if (task.assignees && task.assignees.length > 0) {
                    updateData.assignees = updatedAssignees;
                }

                // Add the updated role-based assignees to update data
                if (
                    task.roleBasedAssignment &&
                    task.roleBasedAssignees &&
                    task.roleBasedAssignees.length > 0
                ) {
                    updateData.roleBasedAssignees = updatedRoleBasedAssignees;
                }

                // Update task in Firestore
                await updateDoc(doc(db, "assignedTasks", task.id), updateData);

                if (onStatusChange) {
                    onStatusChange();
                }
            } else {
                alert("No email changes needed for this task.");
            }
        } catch (err) {
            console.error("Error updating assignee emails:", err);
            alert("Error updating assignee emails: " + err.message);
        } finally {
            setUpdating(false);
        }
    };

    const daysLeftInfo = getDaysLeft(task.endDate || task.dueDate);

    return (
        <div className={`task-card ${getStatusClass()}`} onClick={onClick}>
            {updating && (
                <div className="updating-overlay">
                    <div className="loading-spinner-small"></div>
                    <span>Updating...</span>
                </div>
            )}

            <div className="task-card-header">
                <div className="task-title">{task.title}</div>
                <div className="task-menu-wrapper">
                    <button
                        className="task-menu-toggle"
                        onClick={handleMenuToggle}
                    >
                        <HiOutlineDotsVertical />
                    </button>

                    {showMenu && (
                        <div className="task-menu-dropdown">
                            <button className="menu-item" onClick={onClick}>
                                <HiOutlineEye />
                                View Details
                            </button>

                            {task.statusNormalized === "new" && (
                                <button
                                    className="menu-item"
                                    onClick={(e) =>
                                        updateTaskStatus(e, "in-progress")
                                    }
                                >
                                    <HiOutlinePencil />
                                    Set to In Progress
                                </button>
                            )}

                            {task.statusNormalized === "in-progress" && (
                                <button
                                    className="menu-item"
                                    onClick={(e) =>
                                        updateTaskStatus(e, "completed")
                                    }
                                >
                                    <HiOutlinePencil />
                                    Set to Completed
                                </button>
                            )}

                            {task.statusNormalized === "completed" && (
                                <button
                                    className="menu-item"
                                    onClick={(e) =>
                                        updateTaskStatus(e, "in-progress")
                                    }
                                >
                                    <HiOutlinePencil />
                                    Reopen Task
                                </button>
                            )}

                            <button className="menu-item" onClick={archiveTask}>
                                <HiOutlineArchive />
                                Archive Task
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="task-description">
                {task.description || "No description provided"}
            </div>

            <div className="task-meta">
                <div className="meta-item">
                    <HiOutlineCalendar className="meta-icon" />
                    <span>Due: {formatDate(task.endDate || task.dueDate)}</span>
                    {daysLeftInfo.text && (
                        <span className={`days-left ${daysLeftInfo.className}`}>
                            {daysLeftInfo.text}
                        </span>
                    )}
                </div>

                <div className="meta-item">
                    <HiOutlineUser className="meta-icon" />
                    <span className="assignee">{getAssigneeNames()}</span>
                    {isAdmin && task.emailUpdated && (
                        <span className="email-updated-badge">Updated</span>
                    )}
                </div>
            </div>

            <div className="task-footer">
                <div className="task-status">
                    <span className={`status-badge ${getStatusClass()}`}>
                        {task.status ||
                            (task.statusNormalized === "new"
                                ? "New task"
                                : task.statusNormalized === "in-progress"
                                ? "In progress"
                                : "Completed")}
                    </span>
                </div>

                <div className="task-actions">
                    {(countComments() > 0 || countAttachments() > 0) && (
                        <>
                            {countComments() > 0 && (
                                <div className="action-item">
                                    <span>{countComments()}</span> comments
                                </div>
                            )}

                            {countAttachments() > 0 && (
                                <div className="action-item">
                                    <span>{countAttachments()}</span> files
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {isAdmin && !task.emailUpdated && (
                <div>
                    <button
                        className="menu-item btn-sm"
                        onClick={updateAssigneeEmails}
                    >
                        <HiOutlineMail />
                        Update Assignee Emails
                    </button>
                </div>
            )}
        </div>
    );
};

export default TaskCard;
